import { useEffect, useState } from 'react';
import axios from 'axios';
import { ClipboardList, Loader2, Globe, Building2, Briefcase, Users, MapPin, CheckCircle2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIP_CLS = {
  violet: 'bg-violet-100 text-violet-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
};
const ICON_WRAP_CLS = {
  violet: 'bg-violet-100 text-violet-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  amber: 'bg-amber-100 text-amber-600',
};

const Chips = ({ items, color = 'violet', testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {(items && items.length) ? items.map((it, i) => (
      <span key={i} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${CHIP_CLS[color]}`}>{it}</span>
    )) : <span className="text-sm text-slate-400">Not provided</span>}
  </div>
);

export default function OnboardingDetailsPage({ user, selectedClient }) {
  const isViewingAsAdmin = selectedClient && user.role !== 'client';
  const firmId = isViewingAsAdmin ? selectedClient.firmId : (user.firm?.id || user.firm_id);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    axios.get(`${API}/onboarding/details/${firmId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.status === 403 ? 'You do not have access to this.' : 'Could not load onboarding details.'))
      .finally(() => setLoading(false));
  }, [firmId]);

  if (loading) return <div className="flex items-center gap-2 text-slate-400 p-8" data-testid="onboarding-details-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (error) return <div className="p-8 text-slate-500" data-testid="onboarding-details-error">{error}</div>;
  if (!data) return <div className="p-8 text-slate-400" data-testid="onboarding-details-empty">No onboarding data.</div>;

  const fd = data.firm_details || {};
  const sv = data.services || {};
  const tm = data.target_markets || {};
  const allServices = [...(sv.primary_services || []), ...(sv.custom_services || [])];

  const Card = ({ icon: Icon, color, title, children }) => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-center gap-2.5 mb-4">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${ICON_WRAP_CLS[color]}`}><Icon className="w-5 h-5" /></div>
        <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{title}</h3>
      </div>
      {children}
    </div>
  );

  const Row = ({ label, value }) => (
    <div className="mb-3 last:mb-0">
      <div className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value || <span className="text-slate-400">Not provided</span>}</div>
    </div>
  );

  return (
    <div className="max-w-4xl animate-fadeInUp" data-testid="onboarding-details-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
          <ClipboardList className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Onboarding Details</h1>
          <p className="text-sm text-slate-500">{isViewingAsAdmin ? `${selectedClient.name}'s sign-up form responses` : 'Sign-up form responses'}</p>
        </div>
        <span className={`ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${data.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="onboarding-details-status">
          {data.completed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
          {data.completed ? 'Completed' : 'Incomplete'}
        </span>
      </div>

      <div className="space-y-5">
        <Card icon={Building2} color="violet" title="Firm Details">
          <div className="flex gap-5">
            {fd.logo && <img src={fd.logo} alt="Firm logo" className="w-20 h-20 object-contain rounded-xl border border-slate-100 bg-white flex-shrink-0" data-testid="onboarding-logo" />}
            <div className="flex-1">
              <Row label="Firm / Company name" value={fd.firm_name} />
              <Row label="Website" value={fd.website ? <a href={fd.website.startsWith('http') ? fd.website : `https://${fd.website}`} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5" />{fd.website}</a> : null} />
              <Row label="Description" value={fd.description} />
            </div>
          </div>
        </Card>

        <Card icon={Briefcase} color="emerald" title="Services & Practice Areas">
          <Chips items={allServices} color="emerald" testid="onboarding-services" />
        </Card>

        <Card icon={Users} color="amber" title="Target Markets & Clients">
          <div className="mb-4">
            <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Geographic markets</div>
            <Chips items={tm.geographic_markets} color="amber" testid="onboarding-geo" />
          </div>
          <div className="mb-4">
            <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Target client types</div>
            <Chips items={tm.target_client_types} color="violet" testid="onboarding-clients" />
          </div>
          <Row label="Additional notes" value={tm.additional_notes} />
        </Card>
      </div>
    </div>
  );
}
